import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Droplets, Recycle, Gauge, CloudRain, Waves, Sprout, Activity, Wrench, Package,
} from "lucide-react";
import { SOLUTIONS } from "../lib/content";
import SEO from "../components/SEO";
import SupplierMarquee from "../components/hidroverse/SupplierMarquee";

const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.2, 0.7, 0.2, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };

// Marketing-voice outcome for every system. Brag-number is the visual hero.
const SOL_OUTCOME = {
  purification: {
    icon: Droplets, accent: "#1FB6C1",
    eyebrow: "Purification",
    title: "Any source. Engineered to potable.",
    lede: "Borehole, river, rain, brackish, coastal or municipal — multi-stage trains that deliver SANS 241 water at every outlet, regardless of what the source threw in last week.",
    brag: "SANS 241", bragLbl: "Every outlet",
  },
  wastewater: {
    icon: Recycle, accent: "#42954B",
    eyebrow: "Wastewater",
    title: "Effluent your site can reuse.",
    lede: "Aerobic biological treatment with tertiary polishing. Reuse-ready irrigation effluent. Zero odour. Sized for the rare but inevitable surge.",
    brag: "Reuse-ready", bragLbl: "Effluent grade",
  },
  pumps: {
    icon: Gauge, accent: "#0C2A40",
    eyebrow: "Pumps & Pressure",
    title: "Silent, redundant, always on.",
    lede: "VSD-driven duty + standby + jockey sets. Whisper-rated for lodges, hardened for mines. Designed so a single failure is a service ticket, not an evacuation.",
    brag: "<55 dBA", bragLbl: "At 1 m",
  },
  harvesting: {
    icon: CloudRain, accent: "#1D6A8C",
    eyebrow: "Source & Storage",
    title: "Every drop earns its keep.",
    lede: "Borehole, surface-water, rainwater and coastal intakes. Smart multi-source blending. Yield-tested, partitioned storage for daily, peak and emergency duties.",
    brag: "Multi-source", bragLbl: "Intelligent blending",
  },
  "pool-spa": {
    icon: Waves, accent: "#1FB6C1",
    eyebrow: "Pool & Spa",
    title: "Crystal clear with less chemistry.",
    lede: "AFM® activated glass filtration, salt chlorination, UV-C polish, ozone hybrid, VSD circulation. The wellness register five-star guests feel without noticing why.",
    brag: "≈ 4 µm", bragLbl: "Filtration",
  },
  irrigation: {
    icon: Sprout, accent: "#42954B",
    eyebrow: "Irrigation",
    title: "Landscapes that thrive on less.",
    lede: "Drip, sub-surface drip, micro-jet and pop-up zones. ET-zero smart control. The water economy that turf, citrus and ornamentals actually require.",
    brag: "Up to 95%", bragLbl: "Water efficiency",
  },
  monitoring: {
    icon: Activity, accent: "#0C2A40",
    eyebrow: "Monitoring",
    title: "See the plant from anywhere.",
    lede: "LoRaWAN telemetry, pressure-zone alerting, audit-grade logging. The dashboard that flags a leak before the duty manager hears about it from a guest.",
    brag: "Real-time", bragLbl: "Telemetry",
  },
  rentals: {
    icon: Package, accent: "#1D6A8C",
    eyebrow: "Rentals & As-a-Service",
    title: "Operational expenditure, owned outcomes.",
    lede: "Bridge units, lease-to-own and water-as-a-service contracts on the same equipment we install. Capex becomes opex, asset risk moves to us.",
    brag: "Opex", bragLbl: "Not Capex",
  },
  service: {
    icon: Wrench, accent: "#1D6A8C",
    eyebrow: "Service & SLA",
    title: "Accountable for the lifetime of the install.",
    lede: "Contracted critical-response clause, monthly SANAS sampling, consumables included, pre-positioned spares. The contract that supports the plant, not just sells it.",
    brag: "SLA", bragLbl: "Critical response",
  },
};

export default function Solutions() {
  return (
    <div data-testid="solutions-page">
      <SEO
        title="Services"
        description="Engineered systems for hospitality, mining, agriculture, estates and municipalities: purification, wastewater, pumps, harvesting, pool & spa, irrigation, monitoring, solar cleaning and SLA service."
        path="/services"
      />

      {/* HERO — brand-led, photo, single CTA, no spec strip */}
      <section className="eng-hero">
        <div className="eh-bg" aria-hidden>
          <img src="/library/purification/purification_01.webp" alt="" loading="lazy" decoding="async"/>
        </div>
        <div className="eh-veil" aria-hidden />
        <div className="eh-content">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.h1 variants={fadeUp}>
              Every water discipline.<br />
              <strong>One accountable<br />supplier.</strong>
            </motion.h1>
            <motion.p variants={fadeUp} className="eh-lede">
              Every engineered system, all delivered in-house. Designed against your real
              feed-water and demand profile — not against a brochure. Hand us the whole site,
              or pick the disciplines that matter most.
            </motion.p>
            <motion.div variants={fadeUp} className="eh-actions">
              <Link to="/contact" className="hv-magnetic">Contact us <span aria-hidden>→</span></Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* OUTCOME GRID — light, hybrid theme: warm white bg, navy headings, teal accents */}
      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#FAFAF7", color: "#0C2A40" }}>
        <div className="max-w-[1500px] mx-auto">
          <h2 className="font-display mb-8 md:mb-10" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
            Engineered systems. One water universe.
          </h2>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
            className="spec-grid"
          >
            {SOLUTIONS.map((s) => {
              const o = SOL_OUTCOME[s.slug] || {};
              const Icon = o.icon || Droplets;
              return (
                <motion.div key={s.slug} variants={fadeUp}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="outcome-tile light"
                    style={{ "--ot-accent": o.accent || "#1FB6C1" }}
                    data-testid={`solution-card-${s.slug}`}
                  >
                    <div className="ot-icon"><Icon /></div>
                    <div className="ot-title">{o.eyebrow || s.name}</div>
                    <p className="ot-lede">{o.lede || s.lede}</p>
                    <div className="ot-cta">
                      <span>How we deliver</span>
                      <span className="ot-cta-arrow" aria-hidden>→</span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* SUPPLIER MARQUEE — combined partners strip */}
      <SupplierMarquee tone="light" />

      {/* CONTACT STRIP — formal, single call to action */}
      <section className="cta-strip">
        <div className="csi">
          <div>
            <h3>Brief us on your water needs.</h3>
            <p className="csi-meta">
              For personalised assistance and solutions, contact our specialists.
            </p>
          </div>
          <Link to="/contact" className="hv-magnetic" style={{ justifyContent: "center" }}>
            Contact us <span aria-hidden>→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
